import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Building2, BedDouble, Calendar, IndianRupee, Plus, X, ClipboardList, Wallet, UserCircle, ShieldCheck } from "lucide-react";
import { Stat, NotPartner } from "./VetDashboard";
import QuickActions from "../components/QuickActions";

const HOSTEL_QUICK_ACTIONS = [
  { to: "/app/hostel/bookings", icon: ClipboardList, label: "All bookings",  desc: "Confirm · check-in · check-out", color: "bg-brand/15 text-brand" },
  { to: "/app/hostel/rooms",    icon: BedDouble,      label: "Rooms & rates", desc: "Manage capacity & pricing",        color: "bg-sky/15 text-sky" },
  { to: "/app/payout-setup",    icon: Wallet,         label: "Payouts setup", desc: "Bank · UPI · auto-settle",        color: "bg-leaf/30 text-ink" },
  { to: "/app/profile",         icon: UserCircle,     label: "Edit profile",  desc: "Name · city · photo",             color: "bg-sun/40 text-ink" },
  { to: "/app/kyc",             icon: ShieldCheck,    label: "KYC",           desc: "Verify your hostel",              color: "bg-ink/10 text-ink" },
];

export default function HostelDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ pet_name: "", owner_name: "", days: 3, check_in: "" });

  const load = () => axios.get(`${API}/partner/hostel/dashboard`).then(r => setData(r.data)).catch(() => setData("err"));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    await axios.post(`${API}/partner/hostel/bookings`, { ...form, days: Number(form.days) });
    setOpen(false);
    setForm({ pet_name: "", owner_name: "", days: 3, check_in: "" });
    load();
  };

  if (data === null) return <div className="text-ink-soft">Loading…</div>;
  if (data === "err") return <NotPartner />;

  const { profile, stats, upcoming, recent } = data;

  return (
    <div className="space-y-6" data-testid="hostel-dashboard-page">
      <header className="rounded-3xl bg-gradient-to-br from-ink to-[#1a1a1a] text-white p-6 md:p-8 relative overflow-hidden">
        <div className="relative z-10">
          <div className="text-xs uppercase tracking-wider font-bold opacity-80">Hostel Owner Dashboard</div>
          <h1 className="font-heading text-2xl md:text-3xl font-extrabold">Welcome back, {user?.name?.split(" ")[0]}!</h1>
          <div className="opacity-90 text-sm">{profile.name} · {profile.city}</div>
        </div>
        <Building2 className="absolute right-6 top-6 w-16 h-16 opacity-20"/>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={Calendar} label="Upcoming check-ins" value={stats.upcoming_checkins} color="bg-sky/15 text-sky"/>
        <Stat icon={BedDouble} label="Pets boarded" value={stats.pets_boarded} color="bg-leaf/30 text-ink"/>
        <Stat icon={Building2} label="Occupancy" value={`${stats.occupancy}/${stats.capacity}`} color="bg-sunshine/30 text-ink"/>
        <Stat icon={IndianRupee} label="Revenue (m)" value={`₹${stats.revenue_this_month}`} color="bg-brand/15 text-brand"/>
      </div>

      <QuickActions items={HOSTEL_QUICK_ACTIONS} testid="hostel-quick-actions"/>

      <section className="bg-surface border border-line rounded-3xl p-5 shadow-soft" data-testid="hostel-upcoming">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl font-bold">Upcoming check-ins</h2>
          <button data-testid="add-booking-button" onClick={() => setOpen(true)} className="bg-ink text-white rounded-full px-4 py-2 text-sm font-bold inline-flex items-center gap-2 hover:bg-brand">
            <Plus className="w-4 h-4"/> New booking
          </button>
        </div>
        {upcoming.length === 0 && <div className="text-ink-soft text-sm">No check-ins scheduled.</div>}
        <div className="divide-y divide-line">
          {upcoming.map(b => (
            <div key={b.booking_id} className="py-3 flex items-center justify-between gap-3 text-sm">
              <div>
                <div className="font-bold">{b.pet_name} <span className="text-ink-soft font-normal">· with {b.owner_name}</span></div>
                <div className="text-xs text-ink-soft">{new Date(b.check_in).toLocaleDateString()} · {b.days} day stay</div>
              </div>
              <span className="text-xs font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-leaf/30">Arriving</span>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-surface border border-line rounded-3xl p-5 shadow-soft">
        <h2 className="font-heading text-xl font-bold mb-3">Recent bookings</h2>
        {recent.length === 0 && <div className="text-ink-soft text-sm">No completed bookings yet.</div>}
        <div className="divide-y divide-line">
          {recent.map(b => (
            <div key={b.booking_id} className="py-3 flex items-center justify-between text-sm">
              <div>
                <div className="font-bold">{b.pet_name} · {b.days} days</div>
                <div className="text-xs text-ink-soft">{new Date(b.check_in).toLocaleDateString()} · {b.owner_name}</div>
              </div>
              <span className="text-xs font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-surface-muted">Completed</span>
            </div>
          ))}
        </div>
      </section>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setOpen(false)}>
          <form onSubmit={add} onClick={e=>e.stopPropagation()} className="bg-surface rounded-3xl p-6 w-full max-w-md border border-line shadow-soft" data-testid="add-booking-form">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-extrabold">Add new booking</h3>
              <button type="button" onClick={() => setOpen(false)} className="p-1.5 rounded-full hover:bg-surface-muted"><X className="w-4 h-4"/></button>
            </div>
            <div className="space-y-3">
              <Field label="Pet name"><input required value={form.pet_name} onChange={e=>setForm({...form, pet_name:e.target.value})} className="pinput"/></Field>
              <Field label="Owner name"><input required value={form.owner_name} onChange={e=>setForm({...form, owner_name:e.target.value})} className="pinput"/></Field>
              <Field label="Check-in date"><input type="date" required value={form.check_in} onChange={e=>setForm({...form, check_in:e.target.value})} className="pinput"/></Field>
              <Field label="Days"><input type="number" min="1" required value={form.days} onChange={e=>setForm({...form, days:e.target.value})} className="pinput"/></Field>
            </div>
            <button data-testid="booking-save" className="mt-5 w-full bg-brand text-white rounded-full py-3 font-bold hover:bg-brand-hover">Save booking</button>
          </form>
        </div>
      )}

      <style>{`.pinput{width:100%;border:1px solid #EAE0D5;border-radius:9999px;padding:0.65rem 1rem;background:#FFFDF7;font-family:'Nunito';font-weight:600;color:#2C2C2C;outline:none;}.pinput:focus{border-color:#FF6B35;}`}</style>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase tracking-wider text-ink-soft">{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);
